import i18next from 'i18next'
import { z } from 'zod'

import {
  getIsoYears,
  isBioFuel,
  isDistillateKind,
  ISO_NOT_AVAILABLE,
  isOtherFuel,
  isResidualKind,
} from '../sections/laboratory/laboratoryRules'

const laboratoryTestsSchema = z.record(z.string().optional().nullable())

export const laboratorySchema = z.object({
  bunker: z
    .object({
      labCompany: z.string().optional(),
      testingCompleted: z.any().optional().nullable(),
    })
    .optional(),
  isoYear: z.string().optional(),
  remarks: z.string().optional(),
  sampleSeals: z
    .object({
      supplier: z.string().optional(),
      surveyorVessel: z.string().optional(),
    })
    .optional(),
  tests: laboratoryTestsSchema.optional(),
})

const isValidNumberInput = value =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== '' &&
  !isNaN(Number(value))

const addRequiredNumberIssue = (ctx, key, labelKey) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: i18next.t('rob.survey.bunker.lab.errors.required', {
      field: i18next.t(labelKey),
    }),
    path: ['laboratory', 'tests', key],
  })
}

const addIssue = (ctx, path, messageKey, params) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: i18next.t(messageKey, params),
    path,
  })
}

const addRequiredFieldIssue = (ctx, path, labelKey) => {
  addIssue(ctx, path, 'rob.survey.bunker.lab.errors.required', {
    field: i18next.t(labelKey),
  })
}

export const validateLaboratorySchema = (values, ctx) => {
  const labCompany = values.laboratory?.bunker?.labCompany
  const testingCompleted = values.laboratory?.bunker?.testingCompleted

  if (!String(labCompany || '').trim()) {
    addRequiredFieldIssue(
      ctx,
      ['laboratory', 'bunker', 'labCompany'],
      'rob.survey.bunker.lab.bunker.labCompany'
    )
  }

  if (
    testingCompleted === null ||
    testingCompleted === undefined ||
    String(testingCompleted).trim() === ''
  ) {
    addRequiredFieldIssue(
      ctx,
      ['laboratory', 'bunker', 'testingCompleted'],
      'rob.survey.bunker.lab.bunker.testingCompleted'
    )
  }

  // Cross-section validation: laboratory ISO selection depends on bunker kind and fuel grade.
  const kind = values.bunker?.bargeKind || ''
  const fuelGrade = values.bunker?.fuelGradePerBdn || ''
  const isoYear = values.laboratory?.isoYear || ''
  const tests = values.laboratory?.tests || {}
  const isoOptions = getIsoYears(kind, fuelGrade)
  const otherFuel = isOtherFuel(fuelGrade)
  const bioFuel = isBioFuel(fuelGrade)
  const distillateKind = isDistillateKind(kind)
  const residualKind = isResidualKind(kind)
  const testsEnabled =
    !otherFuel && isoYear !== '' && isoYear !== ISO_NOT_AVAILABLE

  if (!otherFuel && fuelGrade && isoOptions.length > 0 && isoYear === '') {
    addIssue(
      ctx,
      ['laboratory', 'isoYear'],
      'rob.survey.bunker.lab.errors.isoYearRequired'
    )
  }

  if (
    !otherFuel &&
    fuelGrade &&
    isoOptions.length === 0 &&
    isoYear !== ISO_NOT_AVAILABLE
  ) {
    addIssue(
      ctx,
      ['laboratory', 'isoYear'],
      'rob.survey.bunker.lab.errors.isoYearMustBeNA'
    )
  }

  if (
    !otherFuel &&
    isoYear &&
    isoYear !== ISO_NOT_AVAILABLE &&
    !isoOptions.includes(isoYear)
  ) {
    addIssue(
      ctx,
      ['laboratory', 'isoYear'],
      'rob.survey.bunker.lab.errors.isoYearInvalid'
    )
  }

  if (tests.density15 && isValidNumberInput(tests.density15)) {
    const density = Number(tests.density15)
    if (density < 600 || density > 1100) {
      addIssue(
        ctx,
        ['laboratory', 'tests', 'density15'],
        'rob.survey.bunker.lab.errors.densityRange',
        { max: 1100, min: 600 }
      )
    }
  }

  if (!testsEnabled) return

  const commonRequiredFields = [
    ['density15', 'rob.survey.bunker.lab.tests.density15'],
    ['flashpoint', 'rob.survey.bunker.lab.tests.flashpoint'],
    ['pourpoint', 'rob.survey.bunker.lab.tests.pourpoint'],
    ['ashContent', 'rob.survey.bunker.lab.tests.ashContent'],
    ['waterContent', 'rob.survey.bunker.lab.tests.waterContent'],
    ['sulphurContent', 'rob.survey.bunker.lab.tests.sulphurContent'],
    ['aluminium', 'rob.survey.bunker.lab.tests.aluminium'],
    ['silicon', 'rob.survey.bunker.lab.tests.silicon'],
    ['sodium', 'rob.survey.bunker.lab.tests.sodium'],
    ['vanadium', 'rob.survey.bunker.lab.tests.vanadium'],
    ['nickel', 'rob.survey.bunker.lab.tests.nickel'],
    ['iron', 'rob.survey.bunker.lab.tests.iron'],
    ['calcium', 'rob.survey.bunker.lab.tests.calcium'],
    ['zinc', 'rob.survey.bunker.lab.tests.zinc'],
    ['phosphorous', 'rob.survey.bunker.lab.tests.phosphorous'],
    ['magnesium', 'rob.survey.bunker.lab.tests.magnesium'],
    ['lead', 'rob.survey.bunker.lab.tests.lead'],
    ['potassium', 'rob.survey.bunker.lab.tests.potassium'],
    ['netCalorificValue', 'rob.survey.bunker.lab.tests.netCalorificValue'],
    ['ccai', 'rob.survey.bunker.lab.tests.ccai'],
    ['totalAcidNumber', 'rob.survey.bunker.lab.tests.totalAcidNumber'],
  ]

  commonRequiredFields.forEach(([key, label]) => {
    if (!isValidNumberInput(tests[key])) {
      addRequiredNumberIssue(ctx, key, label)
    }
  })

  if (residualKind) {
    const residualRequiredFields = [
      ['viscosity50', 'rob.survey.bunker.lab.tests.viscosity50'],
      ['microCarbonResidue', 'rob.survey.bunker.lab.tests.microCarbonResidue'],
      [
        'totalSedAccelerated',
        'rob.survey.bunker.lab.tests.totalSedAccelerated',
      ],
      ['totalSedPotential', 'rob.survey.bunker.lab.tests.totalSedPotential'],
      ['totalSedExistent', 'rob.survey.bunker.lab.tests.totalSedExistent'],
    ]

    residualRequiredFields.forEach(([key, label]) => {
      if (!isValidNumberInput(tests[key])) {
        addRequiredNumberIssue(ctx, key, label)
      }
    })
  }

  if (distillateKind) {
    const distillateRequiredFields = [
      ['viscosity40', 'rob.survey.bunker.lab.tests.viscosity40'],
      ['mcrt10pctResidue', 'rob.survey.bunker.lab.tests.mcrt10pctResidue'],
      ['cetaneIndex', 'rob.survey.bunker.lab.tests.cetaneIndex'],
      ['lubricity', 'rob.survey.bunker.lab.tests.lubricity'],
    ]

    distillateRequiredFields.forEach(([key, label]) => {
      if (!isValidNumberInput(tests[key])) {
        addRequiredNumberIssue(ctx, key, label)
      }
    })

    if (!String(tests.appearance || '').trim()) {
      addRequiredNumberIssue(
        ctx,
        'appearance',
        'rob.survey.bunker.lab.tests.appearance'
      )
    }
  }

  if (bioFuel) {
    if (!isValidNumberInput(tests.fame)) {
      addRequiredNumberIssue(ctx, 'fame', 'rob.survey.bunker.lab.tests.fame')
    }
    if (!isValidNumberInput(tests.lowHeatingValue)) {
      addRequiredNumberIssue(
        ctx,
        'lowHeatingValue',
        'rob.survey.bunker.lab.tests.lowHeatingValue'
      )
    }
  }
}
